import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Button, SafeHtml, WidgetCard } from '@/shared/ui';

import { reportsModel, useCheckReportSignature } from '@/entities/reports';

import { ReportSignatures } from '@/features/reportSignatures';

import { getContentWithoutHTML } from '../../lib/getContentWithoutHTML';
import { getDraftJsStyles } from '../../lib/getDraftJsStyles';
import { getEditorContentFromValue } from '../../lib/getEditorContentFromValue';
import { getHTMLWithStyles } from '../../lib/getHTMLWithStyles';
import { Editor } from '../Editor/Editor';

import styles from './Conclusion.module.scss';

type ConclusionProps = {
  className?: string;
};

export const Conclusion: FC<ConclusionProps> = (props) => {
  const { className } = props;
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editorValue, setEditorValue] = useState('');

  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const { isMobile } = useMedia();

  const conclusion = report?.LegacyConclusion || '';
  const reportID = report?.ID || '';

  const placeholder = formatMessage({
    id: 'report.conclusion.placeholder',
    defaultMessage: 'Leave a conclusion...',
  });

  useEffect(() => {
    const loadContent = async () => {
      try {
        const { stateToHTML } = await import('draft-js-export-html');
        const editorContent = await getEditorContentFromValue(conclusion);
        const htmlWithoutAlign = stateToHTML(editorContent);
        const htmlWithStyles = getHTMLWithStyles(conclusion, htmlWithoutAlign);
        setHtml(htmlWithStyles);
        setEditorValue(htmlWithStyles);
      } catch (error) {
        console.error('Error loading conclusion:', error);
      }
    };

    loadContent();
  }, [conclusion]);

  const text = getContentWithoutHTML(html);

  const { checkReportSignature } = useCheckReportSignature();

  const handleSave = () => {
    checkReportSignature({
      onSignatureChecked: async () => {
        setLoading(true);

        try {
          const contentState = JSON.stringify(
            await getDraftJsStyles(editorValue),
          );

          const result = await dispatch(
            reportsModel.thunks.setReportConclusion({
              ReportID: reportID,
              LegacyConclusion: contentState,
            }),
          ).unwrap();

          if (result.Report) {
            dispatch(reportsModel.actions.setNewestOne(result.Report));
          }
        } catch (e) {
          console.error(e);
        } finally {
          setLoading(false);
          setIsEditMode(false);
        }
      },
    });
  };

  return (
    <WidgetCard
      className={cn(styles.container, className)}
      data-section="conclusion"
    >
      <div className={cn(styles.conclusionContainer, className)}>
        <h1 className={cn(isMobile && 'h1m')}>
          <FormattedMessage
            id="report.conclusion"
            defaultMessage="Conclusion"
          />
        </h1>
        {text && !isEditMode ? (
          <div className={styles.legacyConclusion}>
            <SafeHtml html={html} />
            <Button
              size="small"
              variant="secondary"
              onClick={() => setIsEditMode(true)}
              className={styles.editButton}
            >
              <FormattedMessage id="global.edit" defaultMessage="Edit" />
            </Button>
          </div>
        ) : (
          <div className={styles.editorContainer}>
            <Editor
              value={editorValue}
              setValue={setEditorValue}
              placeholder={placeholder}
            />
            <div className={styles.buttonsContainer}>
              {text && (
                <Button
                  size="small"
                  variant="tertiary"
                  onClick={() => setIsEditMode(false)}
                >
                  <FormattedMessage
                    id="global.cancel"
                    defaultMessage="Cancel"
                  />
                </Button>
              )}
              <Button
                size="small"
                variant="secondary"
                onClick={handleSave}
                loading={loading}
                disabled={
                  editorValue === html ||
                  (!text && !getContentWithoutHTML(editorValue).trim())
                }
              >
                <FormattedMessage id="global.save" defaultMessage="Save" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {report && <ReportSignatures />}
    </WidgetCard>
  );
};
